import { Divider, Box } from '@mui/material';
import {
  RestoreAzureDiskConfig,
  RestoreEbsVolumeConfig,
  RestoreGceDiskConfig,
} from '@repo/api-gw-sdk';
import type { RestoreJob } from '@repo/api-gw-sdk';

import { PanelWrapper } from '@/components/panels/panelWrapper';
import { useFields } from '@/components/table/hooks';

import { FieldsSection } from './fieldsSection';
import { JobItemFields } from './jobItemFields';
import { JobStatusCard } from './jobStatusCard';
import { OperationSummary } from './operationSummary';
import {
  restoreJobConfigurationFields,
  restoreJobItemFields,
  restoreJobSummaryFromFields,
  restoreJobSummaryToFields,
} from './restoreJobItemFields';
import { VolumeConfigurationSection } from './volumesConfigurationSection';

interface RestoreJobsCardProps {
  job: RestoreJob;
  onClose: () => void;
}

export const RestoreJobsCard = ({ job, onClose }: RestoreJobsCardProps) => {
  return (
    <PanelWrapper
      header={{
        title: 'Job details',
        onClose,
        hideBorder: true,
      }}
      testIdPrefix='restore-jobs-card'
    >
      <RestoreJobsCardContent job={job} />
    </PanelWrapper>
  );
};

const RestoreJobsCardContent = ({ job }: { job: RestoreJob }) => {
  const fromCloudProvider = job.vault?.cloudProvider;
  const toCloudProvider = job.destinationDetails.cloudProvider;
  const configurationFields = useFields(restoreJobConfigurationFields, job);

  const isVolumeConfig = VolumeConfigs.some(
    (config) => job.configuration instanceof config
  );

  return (
    <Box className='pb-[40px]' overflow='auto'>
      <JobItemFields job={job} fields={restoreJobItemFields} />
      <Divider />
      <JobStatusCard job={job} />
      <OperationSummary
        job={job}
        fromTitle='Restore from'
        fromFields={restoreJobSummaryFromFields}
        fromCloudProvider={fromCloudProvider}
        toTitle='Restore to'
        toFields={restoreJobSummaryToFields}
        toCloudProvider={toCloudProvider}
      />
      {job.configuration && !isVolumeConfig && (
        <FieldsSection
          fields={configurationFields}
          title='Instance Configuration'
          defaultExpanded={true}
        />
      )}
      <VolumeConfigurationSection job={job} cloudProvider={toCloudProvider} />
    </Box>
  );
};

const VolumeConfigs = [
  RestoreEbsVolumeConfig,
  RestoreAzureDiskConfig,
  RestoreGceDiskConfig,
];
